import {Component, Inject} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingConfiguration} from '@domain/production-schedule/drawing-configuration';
import {DrawingConfigurationWithColorAndYarnSet} from '@domain/production-schedule/drawing-configuration-with-color-and-yarn-set';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {DataSource} from '@domain/settings/data-source';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {DrawingLibraryHelper} from '@presentation/pages/texedit/drawing-library/drawing-library-new-listview/drawing-library-helper';
import {
  AssertionUtils,
  BaseComponent,
  convertPixelsToHeightInMeter,
  convertToCommercialUnitCentimeter,
  DialogBuilderFactoryService,
  DialogType,
  GridModel,
  Unit,
  UnitValue
} from '@vdw/angular-component-library';
import {ICellRendererAngularComp} from 'ag-grid-angular';
import {AdvancedFilterModel, FilterModel, GridApi, ICellRendererParams, SortModelItem} from 'ag-grid-community';
import {map, takeUntil} from 'rxjs';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../../../plan/production-schedule-plan';
import {GroupedDrawing} from '../../drawings-grouped-view/grouped-drawing';
import {GroupedDrawingsSelectionService} from '../../drawings-grouped-view/grouped-drawings-selection.service';

type DrawingConfigurationForm = FormGroup<{
  quantity: FormControl<number>;
  rollLengthInMeter: FormControl<number>;
}>;

@Component({
  templateUrl: './grouped-design-thumbnails-detail.component.html',
  styleUrls: ['./grouped-design-thumbnails-detail.component.scss']
})
export class GroupedDesignThumbnailsDetailComponent extends BaseComponent implements ICellRendererAngularComp {
  public dataSources: DataSource[];
  public gridApi: GridApi;
  public drawingConfigurations: ListDrawingConfiguration[] = [];
  public productionSchedule: ProductionSchedule;
  public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  public productionScheduleItemMultiplier: number;
  public selectedPath: ProductionSchedulePath;
  public drawingConfigurationsFormArray: FormArray<DrawingConfigurationForm> = null;
  public isFormVisible: boolean[] = [];
  public dataLoaded: boolean = false;

  private masterData: GroupedDrawing;
  private availableSpaceInBuggyInPicks: number;
  private buggyLengthMaxInPicks: number;
  private hasQuantitySuggestionPermission: boolean = false;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Inject(DRAWINGS) private readonly drawings: Drawings,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly groupedDrawingsSelectionService: GroupedDrawingsSelectionService,
    private readonly drawingLibraryHelper: DrawingLibraryHelper,
    private readonly formBuilder: FormBuilder
  ) {
    super();
    this.formBuilder = formBuilder;
  }

  public agInit(params: any): void {
    this.gridApi = params.api;
    this.masterData = params.data;
    this.productionSchedule = params.extraData.productionSchedule;
    this.productionSchedulePathsOfColoredYarnSet = params.extraData.productionSchedulePathsOfColoredYarnSet;
    this.productionScheduleItemMultiplier = params.extraData.productionScheduleItemMultiplier;
    this.selectedPath = params.extraData.selectedPath;
    this.availableSpaceInBuggyInPicks = params.extraData.availableSpaceInBuggyInPicks;
    this.buggyLengthMaxInPicks = params.extraData.buggyLengthMaxInPicks;
    this.hasQuantitySuggestionPermission = params.extraData.hasQuantitySuggestionPermission;

    this.getDrawingConfigurations(params.data.drawingIds, params.data.configurationIds);
  }

  public refresh(_: ICellRendererParams<any, any>): boolean {
    return false;
  }

  public canShowDrawingConfiguration(drawingConfiguration: ListDrawingConfiguration): boolean {
    return !AssertionUtils.isNullOrUndefined(drawingConfiguration);
  }

  public canShowModifiedDate(drawingConfiguration: ListDrawingConfiguration): boolean {
    return !AssertionUtils.isNullOrUndefined(drawingConfiguration.drawing) && !AssertionUtils.isNullOrUndefined(drawingConfiguration.drawing.dateModified);
  }

  public addDrawingToProductionSchedulePath(drawingConfiguration: ListDrawingConfiguration, drawingConfigurationForm: any): void {
    if (!drawingConfigurationForm.valid) {
      return;
    }

    const listDrawing = drawingConfiguration;
    listDrawing.amount = Math.floor(drawingConfigurationForm.getRawValue().quantity / this.productionScheduleItemMultiplier);

    if (this.isShapeRunner(listDrawing.drawing)) {
      listDrawing.rollLengthInMeter = drawingConfigurationForm.getRawValue().rollLengthInMeter;
    }

    if (
      !AssertionUtils.isNullOrUndefined(this.selectedPath) &&
      this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(listDrawing, this.selectedPath.technicalWidthInDents)
    ) {
      this.addDrawingToPath(listDrawing);
    } else if (
      AssertionUtils.isNullOrUndefined(this.selectedPath) &&
      this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent, listDrawing)
    ) {
      this.addDrawingToNewPath(listDrawing);
    } else {
      this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
        titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
        messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
        type: DialogType.INFORMATION
      });
    }
  }

  public selectInputContent(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement) {
      inputElement.select();
    }
  }

  public onFocusInput(index: number): void {
    this.isFormVisible[index] = true;
  }

  public onBlurInput(index: number): void {
    this.isFormVisible[index] = false;
  }

  private getDrawingConfigurations(drawingIds: number[], configurationIds: number[]): void {
    this.getDetailServerSideDataSource(drawingIds, configurationIds);
  }

  private getDetailServerSideDataSource(drawingIds: number[], configurationIds: number[]): void {
    const sortModel: SortModelItem[] = [];
    const startRow: number = 0;
    const endRow: number = 30;
    const currentFilterModel: FilterModel | AdvancedFilterModel = null;

    let filterModel =
      !AssertionUtils.isNullOrUndefined(drawingIds) && !AssertionUtils.isEmpty(drawingIds) && !AssertionUtils.isNullOrUndefined(configurationIds) && !AssertionUtils.isEmpty(configurationIds)
        ? {
            id: {
              filterModels: [null, {values: drawingIds, filterType: 'set'}],
              filterType: 'multi'
            },
            configurationId: {
              filterModels: [null, {values: configurationIds.filter((id: number) => id > 0), filterType: 'set'}],
              filterType: 'multi'
            }
          }
        : currentFilterModel;

    const detailGridModel = new GridModel(startRow, endRow, sortModel, filterModel);

    this.drawings
      .getAllWithoutImageDataFromGridModel(TargetForListOfDrawingsEnum.PRODUCTION_ORDER, detailGridModel, true, 'group')
      .pipe(
        map((listOfDrawings: Drawing[]) => {
          return listOfDrawings.map((drawing: Drawing) => {
            const config = this.getDrawingConfiguration(drawing, this.masterData.quality.name, this.masterData.colorSet?.name, this.masterData.commercialWidthInCM);
            return new ListDrawingConfiguration(drawing, 0, 0, this.masterData.commercialWidthInCM, config);
          });
        }),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe({
        next: (drawingConfigurations: ListDrawingConfiguration[]) => {
          this.drawingConfigurations = drawingConfigurations;
          this.initializeDrawingConfigurationForms();
          this.dataLoaded = true;
        }
      });
  }

  private getDrawingConfiguration(drawing: Drawing, quality: string, colorSetName: string, widthInCM: number): DrawingConfiguration {
    return drawing?.configurations?.find((drawingConfiguration: DrawingConfiguration) => {
      const width = convertToCommercialUnitCentimeter({unit: Unit.MILLIMETER, value: drawingConfiguration.commercialDimensionsInMm.widthInMM} as UnitValue);
      const colorSet = drawingConfiguration instanceof DrawingConfigurationWithColorAndYarnSet ? drawingConfiguration.colorSet : null;
      return drawingConfiguration.quality?.name === quality && colorSet?.name === colorSetName && width === Number(widthInCM);
    });
  }

  private isShapeRunner(drawing: Drawing): boolean {
    return drawing.shape === FixedShape[FixedShape.RUNNER];
  }

  private initializeDrawingConfigurationForms(): void {
    this.drawingConfigurationsFormArray = this.formBuilder.array(
      this.drawingConfigurations.map((listDrawingConfiguration: ListDrawingConfiguration) => this.initializeDrawingConfigurationForm(listDrawingConfiguration))
    );

    this.drawingConfigurationsFormArray.controls.forEach((drawingConfigurationForm: DrawingConfigurationForm, index: number) => {
      const drawingConfiguration = this.drawingConfigurations[index];
      this.checkAndDisableRollLength(drawingConfigurationForm, drawingConfiguration);
    });

    this.isFormVisible = this.drawingConfigurationsFormArray?.controls.map(() => false);
  }

  private initializeDrawingConfigurationForm(listDrawingConfiguration: ListDrawingConfiguration): FormGroup {
    listDrawingConfiguration.rollLengthInMeter = this.getRollLengthInMeter(listDrawingConfiguration);

    return this.formBuilder.group({
      quantity: this.formBuilder.control(this.getSuggestedQuantityForDrawing(listDrawingConfiguration), [Validators.required, Validators.min(this.productionScheduleItemMultiplier)]),
      rollLengthInMeter: this.formBuilder.control(listDrawingConfiguration.rollLengthInMeter ?? 0)
    });
  }

  private checkAndDisableRollLength(drawingConfigurationForm: DrawingConfigurationForm, listDrawingConfiguration: ListDrawingConfiguration): void {
    if (this.isShapeRunner(listDrawingConfiguration.drawing)) {
      drawingConfigurationForm.controls.rollLengthInMeter.enable();
    } else {
      drawingConfigurationForm.controls.rollLengthInMeter.disable();
    }
  }

  private getRollLengthInMeter(config: ListDrawingConfiguration): number | null {
    if (config?.rollLengthInMeter && config?.commercialDimensionsInCm?.heightInCM / 100 < config.rollLengthInMeter) {
      return config.rollLengthInMeter;
    } else if (config?.drawing?.repeated) {
      const rollLengthInMeter = config?.commercialDimensionsInCm?.heightInCM / 100;
      if (!AssertionUtils.isNullOrUndefined(rollLengthInMeter) && !isNaN(rollLengthInMeter)) {
        config.rollLengthInMeter = rollLengthInMeter;
        return config.rollLengthInMeter;
      } else {
        return convertPixelsToHeightInMeter(
          config?.drawing?.dimensionsInPixels?.heightInPx,
          this.productionSchedule?.machineQuality?.pickDensity * 1000,
          this.productionSchedule?.machineQuality?.weaveStructure?.picksPerColorLine
        );
      }
    } else {
      return null;
    }
  }

  private addDrawingToPath(listDrawing: ListDrawingConfiguration): void {
    const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
      this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );

    this.productionSchedulePlan.addDrawingToProductionSchedulePath(listDrawing, this.selectedPath.pathNumber, productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet);
  }

  private addDrawingToNewPath(listDrawing: ListDrawingConfiguration): void {
    this.productionSchedulePlan
      .addDrawingToColoredYarnSet(listDrawing, this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((path: ProductionSchedulePath) => {
        if (!AssertionUtils.isNullOrUndefined(path)) {
          this.selectedPath = path;
          this.groupedDrawingsSelectionService.updateSelectedPath(this.selectedPath);
          this.groupedDrawingsSelectionService.updateSelectedRestPathColoredYarnSet(undefined);
        }
      });
  }

  private getSuggestedQuantityForDrawing(drawingConfiguration: ListDrawingConfiguration): number {
    return this.drawingLibraryHelper.getQuantitySuggestionForPath(
      drawingConfiguration,
      this.productionSchedule,
      this.selectedPath,
      this.availableSpaceInBuggyInPicks,
      this.buggyLengthMaxInPicks,
      true,
      this.productionScheduleItemMultiplier
    );
  }
}
